import React, { useState, useEffect, useMemo } from 'react';
import { InputGroup, Form, Button, Alert, Spinner, Table, Modal, Image } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import axios from 'axios';
import { apiBackendBaseUrl } from 'utils/envsetup';
import { getDefaultReqConfig } from 'request/defaultReqConfig';
import keycloak from 'Keycloak';

function ReferralCode() {
  const [searchInput, setSearchInput] = useState('');
  const [referralData, setReferralData] = useState([]);
  const [generateAsrId, setGenerateAsrId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [copyMsg, setCopyMsg] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  const getReferralDetails = async () => {
    try {
      const url = `${apiBackendBaseUrl}referal/getall`;
      const { data } = await axios.get(url, {
        headers: {
          'X-Data-Token': getDefaultReqConfig(),
          Authorization: `Bearer ${keycloak.token}`,
        },
      });
      if (data) {
        setReferralData(data);
      }
    } catch (error) {
      console.error('Failed to fetch referral data', error);
    }
  };

  const promoCode = useMemo(() => {
    return (
      referralData?.filter(item =>
        Object.values(item).some(value =>
          value?.toString().toLowerCase()?.includes(searchInput?.toLowerCase()),
        ),
      ) || []
    );
  }, [referralData, searchInput]);

  useEffect(() => {
    getReferralDetails();
  }, []);

  useEffect(() => {
    if (successMsg || copyMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg('');
        setCopyMsg('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg, copyMsg]);

  const handleGenerateReferral = async () => {
    if (!generateAsrId) return;
    setLoading(true);
    setError('');
    try {
      const url = `${apiBackendBaseUrl}referal/getcode/${generateAsrId}`;
      const { data } = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${keycloak.token}`,
        },
      });

      if (data) {
        await getReferralDetails();
        setGeneratedCode(data);
        setShowModal(true);
        setGenerateAsrId('');
      } else {
        setError('No promo code received.');
      }
    } catch (err) {
      console.error('Referral generation error:', err);
      setError('Failed to generate promo code.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = value => {
    navigator.clipboard.writeText(value);
    setCopyMsg('Promo code copied to clipboard!');
  };

  const auditColumns = [
    { accessorKey: 'asrId', header: 'ASR ID' },
    {
      accessorKey: 'referalCode',
      header: 'Referral ID',
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="d-flex justify-content-center align-items-center gap-2">
            <span>{value}</span>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleCopy(value)}
              style={{ cursor: 'pointer', background: '#6A2BF3' }}
            >
              <Image src="/assets/svg/copyIcon.svg" />
            </Button>
          </div>
        );
      },
    },
    { accessorKey: 'type', header: 'Type' },
    { accessorKey: 'customerName', header: 'Name' },
    { accessorKey: 'companyName', header: 'Company Name' },
    { accessorKey: 'phone', header: 'Phone' },
    { accessorKey: 'emailId', header: 'Email ID' },
    { accessorKey: 'studioLogin', header: 'Studio Login ID' },
  ];

  const auditTable = useReactTable({
    data: promoCode,
    columns: auditColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="container mt-4" style={{ padding: 0 }}>
      <h4 className="text-center mb-4">Referral Code Management</h4>
      <div className="d-flex flex-column align-items-center w-100">
        {successMsg && <Alert variant="success">{successMsg}</Alert>}
        {copyMsg && <Alert variant="info">{copyMsg}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <InputGroup className="search-bar mb-3">
          <Form.Control
            type="text"
            placeholder="Enter ASR ID to generate Promo Code"
            value={generateAsrId}
            onChange={e => setGenerateAsrId(e.target.value)}
          />
          <Button
            variant="success"
            onClick={handleGenerateReferral}
            disabled={loading}
            style={{ background: 'Grey' }}
          >
            {loading ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <FaPlus /> Generate
              </>
            )}
          </Button>
        </InputGroup>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Form.Control
          type="text"
          placeholder="Search..."
          value={searchInput}
          onChange={e => setSearchInput(e?.target?.value)}
          style={{ width: '250px', marginBottom: '10px' }}
        />
      </div>

      <Table bordered hover responsive className="mt-3 customtable">
        <thead className="table-dark text-center">
          {auditTable.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>{flexRender(header.column.columnDef.header)}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {auditTable.getRowModel().rows.length > 0 ? (
            auditTable.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="text-center small-td-font">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={auditColumns.length} className="text-center">
                No matching records found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex align-items-center gap-2 mt-3">
        <Button
          variant="secondary"
          onClick={() => auditTable.setPageIndex(0)}
          disabled={!auditTable.getCanPreviousPage()}
        >
          ⏮ First
        </Button>
        <Button
          variant="secondary"
          onClick={() => auditTable.previousPage()}
          disabled={!auditTable.getCanPreviousPage()}
        >
          ◀ Prev
        </Button>
        <span>
          Page {auditTable.getState().pagination.pageIndex + 1} of {auditTable.getPageCount()}
        </span>
        <Button
          variant="secondary"
          onClick={() => auditTable.nextPage()}
          disabled={!auditTable.getCanNextPage()}
        >
          Next ▶
        </Button>
        <Button
          variant="secondary"
          onClick={() => auditTable.setPageIndex(auditTable.getPageCount() - 1)}
          disabled={!auditTable.getCanNextPage()}
        >
          ⏭ Last
        </Button>
      </div>

      {/* Modal for displaying generated referral code */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Promo Code</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <h5>Generated promo code is:</h5>
          <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
            <strong>{generatedCode}</strong>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(generatedCode);
                setCopyMsg('Promo code copied to clipboard!');
              }}
              style={{ background: '#6A2BF3' }}
            >
              <Image src="/assets/svg/copyIcon.svg" />
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ReferralCode;
